import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';

import Board from 'webapp/models-custom/board';
import Serviced from 'webapp/models-custom/serviced';

export interface BatchSelectItem {
	id: string | number | null;
	modelName?: string;
	favorite?: boolean;
}

type GetAllItemsFn<T> = () => T[];
type GetAllOptionsFn = () => PopupListItem<string>[];
type DoBatchAction = (actionName: string) => Promise<unknown>;

export interface BatchSelectSettings<T> {
	getAllItems: GetAllItemsFn<T>;
	getOptions: GetAllOptionsFn;
	doBatchAction: DoBatchAction;
	preselectedItems?: T[];
	maxSelectedItems?: number;
	replaceSelectionOnMaxItemsReached?: boolean;
	onMaxItemsReached?: () => void;
	onItemAdded?: (item: T) => void;
	onItemRemoved?: (item: T) => void;
}

export type DeleteAction = 'batchDelete';
export type AddTagAction = 'batchUpdateTags';
export type AddToHomeAction = 'batchAddToHome';
export type AddToDataboardAction = 'batchAddToDataboard';
export type ToggleFavoriteAction = 'batchToggleFavorite';
export type RestoreAction = 'batchRestore';
export type DeletePermanentlyAction = 'batchDeletePermanently';

export default class BatchSelect<T extends BatchSelectItem> extends Serviced {
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;

	@tracked selectedItems: T[] = [];

	getAllItems: GetAllItemsFn<T>;
	getOptions: GetAllOptionsFn;
	doBatchAction: DoBatchAction;
	maxSelectedItems?: number;
	replaceSelectionOnMaxItemsReached?: boolean;
	onMaxItemsReached?: () => void;
	onItemAdded?: (item: T) => void;
	onItemRemoved?: (item: T) => void;

	constructor(options: BatchSelectSettings<T>) {
		super();

		this.getAllItems = options.getAllItems;
		this.getOptions = options.getOptions;
		this.doBatchAction = options.doBatchAction;

		if (options.preselectedItems) {
			this.selectedItems = options.preselectedItems;
		}

		if (options.maxSelectedItems) {
			this.maxSelectedItems = options.maxSelectedItems;
		}

		if (options.replaceSelectionOnMaxItemsReached) {
			this.replaceSelectionOnMaxItemsReached = options.replaceSelectionOnMaxItemsReached;
		}

		if (options.onMaxItemsReached) {
			this.onMaxItemsReached = options.onMaxItemsReached;
		}

		if (options.onItemAdded) {
			this.onItemAdded = options.onItemAdded;
		}

		if (options.onItemRemoved) {
			this.onItemRemoved = options.onItemRemoved;
		}
	}

	get allItems() {
		return this.getAllItems();
	}

	get options() {
		return this.getOptions();
	}

	get isEverythingSelected(): boolean {
		const selectedItemIds = this.selectedItemIds;
		return this.allItems.every((item) => selectedItemIds.includes(this.getItemUniqueId(item)));
	}

	get isEverythingFavorited(): boolean {
		return this.selectedItems.every((item) => !!item.favorite);
	}

	get addToDataboardOption(): PopupListItem<AddToDataboardAction> {
		const selectedItemsCount = this.selectedItems.length,
			isAddToDataboardDisabled = selectedItemsCount > Board.maxBlocks;
		return {
			icon: 'Databoards',
			text: `Add to dashboard (${selectedItemsCount})`,
			action: 'batchAddToDataboard',
			isDisabled: isAddToDataboardDisabled,
			extra: isAddToDataboardDisabled
				? {
						type: 'limitReached',
						text: `Maximum number of metrics allowed on the dashboard is ${Board.maxBlocks}.`,
					}
				: undefined,
		};
	}

	get addToHomeOption(): PopupListItem<AddToHomeAction> {
		return {
			icon: 'Plus',
			text: `Add to Home (${this.selectedItems.length})`,
			action: 'batchAddToHome',
		};
	}

	get toggleFavoriteOption(): PopupListItem<ToggleFavoriteAction> {
		const selectedCount = this.selectedItems.length,
			allFavorites = this.isEverythingFavorited,
			textPrefix = allFavorites ? 'Unf' : 'F',
			cssClass = allFavorites ? 'accent' : '';

		return {
			class: `star ${cssClass}`,
			icon: allFavorites ? 'StarFill' : 'Star',
			text: `${textPrefix}avorite (${selectedCount})`,
			action: 'batchToggleFavorite',
		};
	}

	get addTagsOption(): PopupListItem<AddTagAction> {
		return {
			icon: 'Tag',
			text: `Add tags (${this.selectedItems.length})`,
			action: 'batchUpdateTags',
		};
	}

	get deleteOption(): PopupListItem<DeleteAction> {
		return {
			class: 'delete',
			icon: 'Trash',
			text: `Delete (${this.selectedItems.length})`,
			action: 'batchDelete',
			theme: 'red',
		};
	}

	get deletePermanentlyOption(): PopupListItem<DeletePermanentlyAction> {
		return {
			class: 'delete',
			icon: 'Trash',
			text: `Permanently delete (${this.selectedItems.length})`,
			action: 'batchDeletePermanently',
			theme: 'red',
		};
	}

	get restoreOption(): PopupListItem<RestoreAction> {
		return {
			class: 'restore',
			icon: 'Restore',
			text: `Restore`,
			action: 'batchRestore',
		};
	}

	@cached
	private get selectedItemIds(): string[] {
		return this.selectedItems.map((i) => this.getItemUniqueId(i));
	}

	addItem(item: T) {
		const selectedItems = this.selectedItems;
		if (selectedItems.length === this.maxSelectedItems) {
			if (this.replaceSelectionOnMaxItemsReached) {
				selectedItems.shift();
				this.selectedItems = [...selectedItems, item];

				if (this.onItemAdded) {
					this.onItemAdded(item);
				}
			}

			if (this.onMaxItemsReached) {
				this.onMaxItemsReached();
			}
		} else {
			this.selectedItems = [...selectedItems, item];

			if (this.onItemAdded) {
				this.onItemAdded(item);
			}
		}
	}

	removeItem(item: T) {
		this.selectedItems = this.selectedItems.filter((i) => i !== item);

		if (this.onItemRemoved) {
			this.onItemRemoved(item);
		}
	}

	isItemSelected(item: T): boolean {
		const itemUniqueId = this.getItemUniqueId(item);
		return this.selectedItemIds.some((i) => i === itemUniqueId);
	}

	private getItemUniqueId(item: T): string {
		const modelName = item.modelName;
		return `${item.id}${modelName ? `_${modelName}` : ''}`;
	}

	@action
	selectAll() {
		this.selectedItems = this.allItems.slice(0, this.maxSelectedItems);
	}

	@action
	unselectAll() {
		this.selectedItems = [];
	}

	@action
	toggleSelectAll(isSelectAll: boolean) {
		if (isSelectAll) {
			this.selectAll();
		} else {
			this.unselectAll();
		}
	}

	@action
	changeSelection(model: T, isSelected: boolean) {
		if (isSelected) {
			this.addItem(model);
		} else {
			this.removeItem(model);
		}
	}

	@action
	triggerBatchAction(actionName: string) {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.doBatchAction(actionName)
			.then(this.unselectAll)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
}

// Helper type to accept any BatchSelect where T extends BatchSelectItem
// This works around TypeScript's invariant generics by using a type that
// accepts any BatchSelect instance (since all BatchSelect types share the same structure)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyBatchSelect = BatchSelect<any>;
